/**
 * Sound Effects Utility
 * Provides simple sound effects for UI interactions
 */

// Create audio context for web audio API
let audioContext: AudioContext | null = null;

const getAudioContext = (): AudioContext => {
    if (!audioContext) {
        audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContext;
};

/**
 * Play a pleasant notification chime
 * Uses Web Audio API to generate a soft, pleasant tone
 */
export const playNotificationChime = (volume: number = 0.3): void => {
    try {
        const ctx = getAudioContext();
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        // Connect nodes
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        // Configure oscillator for a pleasant chime
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(800, ctx.currentTime); // Start at 800Hz
        oscillator.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.1); // Drop to 400Hz

        // Configure volume envelope
        gainNode.gain.setValueAtTime(0, ctx.currentTime);
        gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01); // Quick attack
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3); // Gentle decay

        // Play the sound
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
    } catch (error) {
        console.warn('Failed to play notification sound:', error);
    }
};

/**
 * Play a success sound (higher pitched, more cheerful)
 */
export const playSuccessSound = (volume: number = 0.3): void => {
    try {
        const ctx = getAudioContext();
        
        // Create two oscillators for a richer sound
        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gainNode = ctx.createGain();

        osc1.connect(gainNode);
        osc2.connect(gainNode);
        gainNode.connect(ctx.destination);

        // First note
        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
        
        // Harmony
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(659.25, ctx.currentTime); // E5

        // Volume envelope
        gainNode.gain.setValueAtTime(0, ctx.currentTime);
        gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);

        osc1.start(ctx.currentTime);
        osc2.start(ctx.currentTime);
        osc1.stop(ctx.currentTime + 0.4);
        osc2.stop(ctx.currentTime + 0.4);
    } catch (error) {
        console.warn('Failed to play success sound:', error);
    }
};

/**
 * Check if sound is enabled in settings
 */
export const isSoundEnabled = (): boolean => {
    try {
        const settings = localStorage.getItem('playful_toast_settings');
        if (settings) {
            const parsed = JSON.parse(settings);
            return parsed.soundEnabled !== false; // Default to true
        }
        return true;
    } catch {
        return true;
    }
};

/**
 * Play notification sound if enabled
 */
export const playNotificationIfEnabled = (): void => {
    if (isSoundEnabled()) {
        playNotificationChime();
    }
};

export default {
    playNotificationChime,
    playSuccessSound,
    isSoundEnabled,
    playNotificationIfEnabled,
};

